/**
 * One owner for "may the student walk right now?".
 *
 * `player.enabled` used to be flipped from five places - splash entry, tool
 * open, tool close, the skid dialog's open and close - and every new surface
 * that captured the keyboard had to remember to be a sixth. Now the rule is
 * derived, in one place: the student walks once they have entered the fab and
 * while nothing holds a lock. Every modal surface acquires a lock while it is
 * up and releases it on the way down; overlapping surfaces just count.
 */

interface Walkable {
  enabled: boolean
}

let target: Walkable | null = null
let entered = false
let locks = 0
const enteredListeners = new Set<() => void>()

function apply(): void {
  if (target) target.enabled = entered && locks === 0
}

/** Point the lock at the walk controller. Once, at boot. */
export function bindWalkTarget(walkable: Walkable): void {
  target = walkable
  apply()
}

/** The student has crossed the airlock (splash dismissed or place restored). */
export function setEntered(on: boolean): void {
  if (entered === on) return
  entered = on
  apply()
  for (const fn of [...enteredListeners]) fn()
}

export function walkEntered(): boolean {
  return entered
}

/** Watch the entered flag - the shell's pre-enter chrome hangs off it. */
export function onEnteredChange(fn: () => void): () => void {
  enteredListeners.add(fn)
  return () => {
    enteredListeners.delete(fn)
  }
}

/**
 * Freeze the walk while a surface owns the input. Returns the release, which
 * is safe to call more than once - a dialog that closes twice must not free
 * somebody else's lock.
 */
export function acquireWalkLock(): () => void {
  locks++
  apply()
  let released = false
  return () => {
    if (released) return
    released = true
    locks--
    apply()
  }
}
